import { useEffect } from 'react';

import { CPF_FIELD_NAME, formatCpf } from '../modules/requestForm/cpfField';

/**
 * Sets the value through the native setter so React's value tracker notices
 * the change and the package's controlled input receives the masked value
 * on its own `onChange`.
 */
const setNativeInputValue = (input: HTMLInputElement, value: string) => {
  const descriptor = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  );

  if (descriptor?.set) {
    descriptor.set.call(input, value);
  } else {
    input.value = value;
  }
};

const isCpfInput = (target: EventTarget | null): target is HTMLInputElement =>
  target instanceof HTMLInputElement && target.id === CPF_FIELD_NAME;

/**
 * Applies the `000.000.000-00` mask to the CPF field rendered by
 * `react-json-form`, which has no mask for `text` inputs. Listens on the
 * document in the capture phase, so it runs before React's own listener and
 * the package only ever sees the formatted value.
 */
export const useCpfMask = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleInput = (event: Event) => {
      const { target } = event;

      if (!isCpfInput(target)) {
        return;
      }

      const formatted = formatCpf(target.value);

      if (formatted === target.value) {
        return;
      }

      event.stopImmediatePropagation();
      setNativeInputValue(target, formatted);
      target.dispatchEvent(new Event('input', { bubbles: true }));
    };

    document.addEventListener('input', handleInput, true);

    return () => {
      document.removeEventListener('input', handleInput, true);
    };
  }, [enabled]);
};
